import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { LlmProviderCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { InputStringList } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type LlmProviderCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  name: string;
  nameError?: string;
  codeSelectedValue?: { displayId: string; id: string } | null;
  onCodeChange?: (value: string) => void;
  code: string;
  codeError?: string;
  requiresApiKeySelectedValue?: { displayId: string; id: string } | null;
  onRequiresApiKeyChange?: (value: boolean) => void;
  requiresApiKey: boolean;
  requiresApiKeyError?: string;
  requiresBaseUrlSelectedValue?: { displayId: string; id: string } | null;
  onRequiresBaseUrlChange?: (value: boolean) => void;
  requiresBaseUrl: boolean;
  requiresBaseUrlError?: string;
  defaultModelsSelectedValue?: { displayId: string; id: string } | null;
  onDefaultModelsChange?: (value: string[]) => void;
  defaultModels: string[];
  defaultModelsError?: string;
  logoUrlSelectedValue?: { displayId: string; id: string } | null;
  onLogoUrlChange?: (value: string) => void;
  logoUrl: string;
  logoUrlError?: string;
  llmProviderCreateStore: LlmProviderCreateStore;
};

export const LlmProviderCreateForm: React.FC<LlmProviderCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name = "",
      nameError,
      onNameChange = () => {},
      code = "",
      codeError,
      onCodeChange = () => {},
      requiresApiKey = false,
      requiresApiKeyError,
      onRequiresApiKeyChange = () => {},
      requiresBaseUrl = false,
      requiresBaseUrlError,
      onRequiresBaseUrlChange = () => {},
      defaultModels = [],
      defaultModelsError,
      onDefaultModelsChange = () => {},
      logoUrl = "",
      logoUrlError,
      onLogoUrlChange = () => {},
      llmProviderCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="LlmProvider"
              mode="create"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "code":
                    onCodeChange?.(valor);
                    break;
                  case "requiresApiKey":
                    onRequiresApiKeyChange?.(valor);
                    break;
                  case "requiresBaseUrl":
                    onRequiresBaseUrlChange?.(valor);
                    break;
                  case "defaultModels":
                    onDefaultModelsChange?.(valor);
                    break;
                  case "logoUrl":
                    onLogoUrlChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={llmProviderCreateStore.getCardErrors("null")}
              title={langStore.groupLlmProviderLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name}
                  error={Boolean(nameError)}
                  warning={
                    !nameError &&
                    Boolean(llmProviderCreateStore.warnings["name"])
                  }
                  helperText={
                    nameError ||
                    llmProviderCreateStore.warnings["name"]?.message
                  }
                  label={langStore.LlmProviderNameLabel}
                  onChange={(value: string) => onNameChange(value)}
                  description={langStore.LlmProviderNameDescription}
                />

                <InputString
                  value={code}
                  error={Boolean(codeError)}
                  warning={
                    !codeError &&
                    Boolean(llmProviderCreateStore.warnings["code"])
                  }
                  helperText={
                    codeError ||
                    llmProviderCreateStore.warnings["code"]?.message
                  }
                  label={langStore.LlmProviderCodeLabel}
                  onChange={(value: string) => onCodeChange(value)}
                  description={langStore.LlmProviderCodeDescription}
                />
                <InputBoolean
                  value={requiresApiKey}
                  error={Boolean(requiresApiKeyError)}
                  helperText={
                    requiresApiKeyError ||
                    llmProviderCreateStore.warnings["requiresApiKey"]?.message
                  }
                  label={langStore.LlmProviderRequiresApiKeyLabel}
                  description={langStore.LlmProviderRequiresApiKeyDescription}
                  onChange={(value: boolean) => onRequiresApiKeyChange(value)}
                />
                <InputBoolean
                  value={requiresBaseUrl}
                  error={Boolean(requiresBaseUrlError)}
                  helperText={
                    requiresBaseUrlError ||
                    llmProviderCreateStore.warnings["requiresBaseUrl"]?.message
                  }
                  label={langStore.LlmProviderRequiresBaseUrlLabel}
                  description={langStore.LlmProviderRequiresBaseUrlDescription}
                  onChange={(value: boolean) => onRequiresBaseUrlChange(value)}
                />
                <InputStringList
                  value={defaultModels}
                  error={Boolean(defaultModelsError)}
                  helperText={
                    defaultModelsError ||
                    llmProviderCreateStore.warnings["defaultModels"]?.message
                  }
                  label={langStore.LlmProviderDefaultModelsLabel}
                  description={langStore.LlmProviderDefaultModelsDescription}
                  onChange={(value: string[]) => onDefaultModelsChange(value)}
                />

                <InputString
                  value={logoUrl}
                  error={Boolean(logoUrlError)}
                  warning={
                    !logoUrlError &&
                    Boolean(llmProviderCreateStore.warnings["logoUrl"])
                  }
                  helperText={
                    logoUrlError ||
                    llmProviderCreateStore.warnings["logoUrl"]?.message
                  }
                  label={langStore.LlmProviderLogoUrlLabel}
                  onChange={(value: string) => onLogoUrlChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.LlmProviderLogoUrlDescription}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
